import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import {
    ChevronLeft,
    ChevronRight,
    Link2,
    MessageCircle,
    X,
} from "lucide-react";
import React, { useState } from "react";
import { FaFacebook, FaInstagram, FaWhatsapp } from "react-icons/fa";
import { ShareButton } from "../SocialShareButton";
import { useSocialEmbedLoader } from "@/hooks/useSocialEmbedLoader";

type SocialPlatform = "twitter" | "facebook" | "instagram";

const XLogo = (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
        <g>
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
        </g>
    </svg>
);

const TwitterCard = () => {
    const { containerRef: twitterContainerRef, sdkLoaded: twitterSdkLoaded } =
        useSocialEmbedLoader("twitter");
    return (
        <Card className="flex flex-col h-full bg-white dark:bg-black border-0 shadow-none rounded-none">
            <CardContent className="flex-grow" ref={twitterContainerRef}>
                {!twitterSdkLoaded && <p>Loading X (Twitter) embed...</p>}
                <div
                    className="fb-page w-full"
                    data-href="https://twitter.com/maharevenue/status/1844309274593460384?ref_src=twsrc%5Etfw"
                    data-tabs="timeline"
                    data-width="500"
                    data-height="500"
                    data-small-header="false"
                    data-adapt-container-width="true"
                    data-hide-cover="false"
                    data-show-facepile="true"
                >
                    <blockquote className="twitter-tweet">
                        <p lang="en" dir="ltr">
                            The Commissioner of the Nashik Division has initiated
                            disqualification action against the Sarpanch in connection with
                            illegal excavation of minor minerals.
                            <a href="https://twitter.com/hashtag/MaharashtraGovernment?src=hash&amp;ref_src=twsrc%5Etfw">
                                #MaharashtraGovernment
                            </a>
                            <a href="https://twitter.com/hashtag/RevenueDepartment?src=hash&amp;ref_src=twsrc%5Etfw">
                                #RevenueDepartment
                            </a>
                            <a href="https://twitter.com/hashtag/NewsToday?src=hash&amp;ref_src=twsrc%5Etfw">
                                #NewsToday
                            </a>
                            <a href="https://t.co/WcChgrvLb2">pic.twitter.com/WcChgrvLb2</a>
                        </p>
                        &mdash; Revenue Department, Government of Maharashtra (@maharevenue){" "}
                        <a href="https://twitter.com/maharevenue/status/1844309274593460384?ref_src=twsrc%5Etfw">
                            October 10, 2024
                        </a>
                    </blockquote>
                </div>
                {/* <script async src="https://platform.twitter.com/widgets.js"></script> */}
            </CardContent>
            <CardFooter className="flex justify-between items-center text-sm text-muted-foreground">
                <div className="flex space-x-4">
                    <button className="flex items-center space-x-1 hover:text-blue-500">
                        <MessageCircle size={16} /> <span>Reply</span>
                    </button>
                    <button className="flex items-center space-x-1 hover:text-blue-500">
                        <Link2 size={16} /> <span>Copy link</span>
                    </button>
                </div>
                <a href="#" className="text-blue-500 hover:underline">
                    Read more on X
                </a>
            </CardFooter>
        </Card>
    );
};

const FacebookCard = () => {
    const { containerRef: fbContainerRef, sdkLoaded: fbSdkLoaded } =
        useSocialEmbedLoader("facebook");

    return (
        <Card className="flex flex-col h-full bg-white dark:bg-gray-800 border-0 shadow-none rounded-none">
            <CardContent ref={fbContainerRef} className="flex-grow">
                {!fbSdkLoaded && <p>Loading Facebook widget...</p>}
                <div id="fb-root"></div>
                <div
                    className="fb-page w-full"
                    data-href="https://www.facebook.com/maharevenue"
                    data-tabs="timeline"
                    data-width="1000"
                    data-height="800"
                    data-small-header="false"
                    data-adapt-container-width="true"
                    data-hide-cover="false"
                    data-show-facepile="true"
                >
                    <blockquote
                        cite="https://www.facebook.com/maharevenue"
                        className="fb-xfbml-parse-ignore"
                    >
                        <a
                            href="http://www.facebook.com/profile.php?id=61579305474012"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline-offset-2 hover:underline"
                            aria-label="Opens in a new tab"
                        >
                            Environment and Climate Change Department, Maharashtra
                        </a>
                    </blockquote>
                </div>
            </CardContent>
        </Card>
    );
};

const InstagramCard = () => {
    const { containerRef: instagramContainerRef, sdkLoaded: instagramSdkLoaded } =
        useSocialEmbedLoader("instagram");
    return (
        <Card className="flex flex-col h-full border-0 shadow-none rounded-none">
            <CardContent
                ref={instagramContainerRef}
                className="flex-grow flex items-center justify-center bg-gray-100 dark:bg-gray-800/50 rounded-lg p-8"
            >
                {!instagramSdkLoaded && <p>Loading Instagram post...</p>}
                <blockquote
                    className="instagram-media"
                    data-instgrm-captioned
                    data-instgrm-permalink="https://www.instagram.com/p/DA8FRDtT5V7/?utm_source=ig_embed&amp;utm_campaign=loading"
                    data-instgrm-version="14"
                    style={{
                        background: "#FFF",
                        border: 0,
                        borderRadius: "3px",
                        boxShadow:
                            "0 0 1px 0 rgba(0,0,0,0.5), 0 1px 10px 0 rgba(0,0,0,0.15)",
                        margin: "1px",
                        maxWidth: "540px",
                        minWidth: "326px",
                        padding: 0,
                        width: "calc(100% - 2px)",
                    }}
                >
                    <div style={{ padding: "16px" }}>
                        <a
                            href="https://www.instagram.com/p/DA8FRDtT5V7/?utm_source=ig_embed&amp;utm_campaign=loading"
                            style={{
                                background: "#FFFFFF",
                                lineHeight: 0,
                                padding: "0 0",
                                textAlign: "center",
                                textDecoration: "none",
                                width: "100%",
                            }}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                }}
                            >
                                <div
                                    style={{
                                        backgroundColor: "#F4F4F4",
                                        borderRadius: "50%",
                                        flexGrow: 0,
                                        height: "40px",
                                        marginRight: "14px",
                                        width: "40px",
                                    }}
                                ></div>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        flexGrow: 1,
                                        justifyContent: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            backgroundColor: "#F4F4F4",
                                            borderRadius: "4px",
                                            flexGrow: 0,
                                            height: "14px",
                                            marginBottom: "6px",
                                            width: "100px",
                                        }}
                                    ></div>
                                    <div
                                        style={{
                                            backgroundColor: "#F4F4F4",
                                            borderRadius: "4px",
                                            flexGrow: 0,
                                            height: "14px",
                                            width: "60px",
                                        }}
                                    ></div>
                                </div>
                            </div>

                            <div style={{ padding: "19% 0" }}></div>

                            <div
                                style={{
                                    display: "block",
                                    height: "50px",
                                    margin: "0 auto 12px",
                                    width: "50px",
                                }}
                            >
                                {/* SVG kept same */}
                            </div>

                            <div style={{ paddingTop: "8px" }}>
                                <div
                                    style={{
                                        color: "#3897f0",
                                        fontFamily: "Arial, sans-serif",
                                        fontSize: "14px",
                                        fontStyle: "normal",
                                        fontWeight: 550,
                                        lineHeight: "18px",
                                    }}
                                >
                                    View this post on Instagram
                                </div>
                            </div>

                            <div style={{ padding: "12.5% 0" }}></div>

                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    marginBottom: "14px",
                                    alignItems: "center",
                                }}
                            >
                                {/* Left buttons */}
                                <div>
                                    <div
                                        style={{
                                            backgroundColor: "#F4F4F4",
                                            borderRadius: "50%",
                                            height: "12.5px",
                                            width: "12.5px",
                                            transform: "translateX(0px) translateY(7px)",
                                        }}
                                    ></div>
                                    <div
                                        style={{
                                            backgroundColor: "#F4F4F4",
                                            height: "12.5px",
                                            transform:
                                                "rotate(-45deg) translateX(3px) translateY(1px)",
                                            width: "12.5px",
                                            flexGrow: 0,
                                            marginRight: "14px",
                                            marginLeft: "2px",
                                        }}
                                    ></div>
                                    <div
                                        style={{
                                            backgroundColor: "#F4F4F4",
                                            borderRadius: "50%",
                                            height: "12.5px",
                                            width: "12.5px",
                                            transform: "translateX(9px) translateY(-18px)",
                                        }}
                                    ></div>
                                </div>

                                {/* Middle */}
                                <div style={{ marginLeft: "8px" }}>
                                    <div
                                        style={{
                                            backgroundColor: "#F4F4F4",
                                            borderRadius: "50%",
                                            flexGrow: 0,
                                            height: "20px",
                                            width: "20px",
                                        }}
                                    ></div>
                                    <div
                                        style={{
                                            width: 0,
                                            height: 0,
                                            borderTop: "2px solid transparent",
                                            borderLeft: "6px solid #f4f4f4",
                                            borderBottom: "2px solid transparent",
                                            transform:
                                                "translateX(16px) translateY(-4px) rotate(30deg)",
                                        }}
                                    ></div>
                                </div>

                                {/* Right */}
                                <div style={{ marginLeft: "auto" }}>
                                    <div
                                        style={{
                                            width: 0,
                                            borderTop: "8px solid #F4F4F4",
                                            borderRight: "8px solid transparent",
                                            transform: "translateY(16px)",
                                        }}
                                    ></div>
                                    <div
                                        style={{
                                            backgroundColor: "#F4F4F4",
                                            flexGrow: 0,
                                            height: "12px",
                                            width: "16px",
                                            transform: "translateY(-4px)",
                                        }}
                                    ></div>
                                    <div
                                        style={{
                                            width: 0,
                                            height: 0,
                                            borderTop: "8px solid #F4F4F4",
                                            borderLeft: "8px solid transparent",
                                            transform: "translateY(-4px) translateX(8px)",
                                        }}
                                    ></div>
                                </div>
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    flexGrow: 1,
                                    justifyContent: "center",
                                    marginBottom: "24px",
                                }}
                            >
                                <div
                                    style={{
                                        backgroundColor: "#F4F4F4",
                                        borderRadius: "4px",
                                        flexGrow: 0,
                                        height: "14px",
                                        marginBottom: "6px",
                                        width: "224px",
                                    }}
                                ></div>
                                <div
                                    style={{
                                        backgroundColor: "#F4F4F4",
                                        borderRadius: "4px",
                                        flexGrow: 0,
                                        height: "14px",
                                        width: "144px",
                                    }}
                                ></div>
                            </div>
                        </a>

                        <p
                            style={{
                                color: "#c9c8cd",
                                fontFamily: "Arial, sans-serif",
                                fontSize: "14px",
                                lineHeight: "17px",
                                marginBottom: 0,
                                marginTop: "8px",
                                overflow: "hidden",
                                padding: "8px 0 7px",
                                textAlign: "center",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            <a
                                href="https://www.instagram.com/p/DA8FRDtT5V7/?utm_source=ig_embed&amp;utm_campaign=loading"
                                style={{
                                    color: "#c9c8cd",
                                    fontFamily: "Arial, sans-serif",
                                    fontSize: "14px",
                                    fontStyle: "normal",
                                    fontWeight: "normal",
                                    lineHeight: "17px",
                                    textDecoration: "none",
                                }}
                                target="_blank"
                                rel="noreferrer"
                            >
                                A post shared by Revenue Department, Government of Maharashtra
                                (@maharevenue)
                            </a>
                        </p>
                    </div>
                </blockquote>
                {/* <script async src="//www.instagram.com/embed.js"></script> */}
            </CardContent>
        </Card>
    );
};

export const SocialMediaHub = () => {
    const [activeTab, setActiveTab] = useState<SocialPlatform | null>(null);
    const [isNavVisible, setIsNavVisible] = useState(true);

    const panelContent = {
        twitter: { title: "Twitter Feed", component: <TwitterCard /> },
        facebook: { title: "Facebook Feed", component: <FacebookCard /> },
        instagram: { title: "Instagram Feed", component: <InstagramCard /> },
    };

    const handleTabClick = (platform: SocialPlatform) => {
        setActiveTab((prev) => (prev === platform ? null : platform));
    };

    const closePanel = () => setActiveTab(null);

    const showSocialNav = isNavVisible && !activeTab;

    const handlWhatsappClick = () => {
        const whatsappUrl = "https://wa.me/9421200000";
        window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    };

    return (
        <>
            <div className="fixed top-1/2 -translate-y-1/2 right-0 z-40 flex flex-col items-center gap-1 sm:gap-2">
                <div
                    className={`transition-all duration-300 ease-in-out flex flex-col gap-1 sm:gap-2
      ${showSocialNav
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 -translate-y-10 pointer-events-none"
                        }`}
                >
                    {/* Twitter Button */}
                    <Button
                        onClick={() => handleTabClick("twitter")}
                        size="icon"
                        className="h-8 w-8 sm:h-10 sm:w-10 shadow-md rounded-r-none"
                        aria-label="Open Twitter Feed"
                    >
                        <XLogo className="h-4 w-4 sm:h-5 sm:w-5 fill-current" />
                    </Button>

                    {/* Facebook Button */}
                    <Button
                        onClick={() => handleTabClick("facebook")}
                        size="icon"
                        className="h-8 w-8 sm:h-10 sm:w-10 bg-blue-600 hover:bg-blue-700 shadow-md rounded-r-none"
                        aria-label="Open Facebook Feed"
                    >
                        <FaFacebook className="h-4 w-4 sm:h-5 sm:w-5" />
                    </Button>

                    {/* Instagram Button */}
                    <Button
                        onClick={() => handleTabClick("instagram")}
                        size="icon"
                        className="h-8 w-8 sm:h-10 sm:w-10 bg-pink-600 hover:bg-pink-700 shadow-md rounded-r-none"
                        aria-label="Open Instagram Feed"
                    >
                        <FaInstagram className="h-4 w-4 sm:h-5 sm:w-5" />
                    </Button>

                    <Button
                        onClick={handlWhatsappClick}
                        size="icon"
                        className="h-8 w-8 sm:h-10 sm:w-10 bg-green-500 hover:bg-green-600 shadow-md rounded-r-none"
                        aria-label="Open Instagram Feed"
                    >
                        <FaWhatsapp className="h-4 w-4 sm:h-5 sm:w-5" />
                    </Button>

                    <ShareButton className="h-8 w-8 sm:h-10 sm:w-10 shadow-md rounded-r-none" />
                </div>

                {/* Master Toggle Button */}
                <Button
                    onClick={() => setIsNavVisible((prev) => !prev)}
                    size="icon"
                    variant="outline"
                    className="h-8 w-8 sm:h-10 sm:w-10 shadow-lg bg-gray-300 border border-gray-400 rounded-r-none"
                    aria-label={
                        isNavVisible
                            ? "Hide social media buttons"
                            : "Show social media buttons"
                    }
                >
                    {isNavVisible ? (
                        <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
                    ) : (
                        <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                </Button>
            </div>
            {/* Overlay */}
            <div
                onClick={closePanel}
                className={`fixed inset-0 bg-black/60 z-40 transition-opacity duration-300 ease-in-out
          ${activeTab ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            />

            {/* Sliding Panel */}
            <aside
                className={`fixed top-0 left-0 h-full w-full max-w-xl transform bg-gray-100 dark:bg-gray-950 z-50 shadow-2xl transition-transform duration-500 ease-in-out
          ${activeTab ? "translate-x-0" : "-translate-x-full"}`}
            >
                <div className="h-full flex flex-col">
                    <div className="flex justify-between items-center p-4 sm:p-6 border-b flex-shrink-0">
                        <h2 className="text-2xl font-bold">
                            {activeTab ? panelContent[activeTab].title : "Social Media"}
                        </h2>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={closePanel}
                            aria-label="Close Social Media Hub"
                        >
                            <X className="h-6 w-6" />
                        </Button>
                    </div>
                    <div className="flex-grow overflow-y-auto">
                        {activeTab && panelContent[activeTab].component}
                    </div>
                </div>
            </aside>
        </>
    );
};
